<template>
  <modal-vue
    @close="handleToggleModal"
    :modalActive="modals[modalName]"
    maxWidth="max-w-[525px]"
    :hasHeaderBorder="true"
  >
    <template #header>
      <p class="font-bold text-left">Excluir Escala?</p>
    </template>
    <div class="flex flex-col gap-4 text-left">
      <p class="text-base font-normal">
        Tem certeza que deseja excluir a escala "<span class="font-bold">{{ scheduleName }}</span
        >"?<br />
        Essa ação não poderá ser desfeita!
      </p>
      <p
        v-if="isLoadingPreview"
        class="text-sm text-gray-500"
        data-testid="remove-schedule-preview-loading"
      >
        Verificando os plantões futuros da escala...
      </p>
      <div
        v-else-if="preview"
        class="flex flex-col gap-1 rounded border border-gray-200 bg-gray-50 p-3 text-sm"
        data-testid="remove-schedule-preview"
      >
        <template v-if="preview.futureStations === 0">
          <p>Esta escala não tem plantões futuros.</p>
        </template>
        <template v-else>
          <p>
            Esta escala tem <span class="font-bold">{{ futureStationsText }}</span
            >:
          </p>
          <ul class="list-disc pl-5">
            <li v-if="preview.futureVacant > 0">{{ futureVacantText }}</li>
            <li v-if="preview.futureWithDoctor > 0">{{ futureWithDoctorText }}</li>
            <li v-if="preview.futureUnidentified > 0">{{ futureUnidentifiedText }}</li>
          </ul>
        </template>
        <p class="text-gray-600">Os plantões passados ficam guardados como histórico.</p>
      </div>
      <p
        v-if="blockedMessage"
        class="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700"
        data-testid="remove-schedule-blocked"
      >
        {{ blockedMessage }}
      </p>
      <p class="text-base font-normal">É necessário digitar o nome da escala para excluí-la</p>
      <v-text-field
        v-model="confirmationText"
        :placeholder="scheduleName"
        variant="outlined"
        density="compact"
        hide-details
        data-testid="remove-schedule-confirmation-input"
      />
    </div>
    <template #footer>
      <div class="flex gap-4">
        <filled-button class="flex-1" @click="handleToggleModal" text="Cancelar" />
        <outline-button
          class="flex-1 !text-red-600 !border-red-600 hover:!bg-red-50 hover:!text-red-700"
          :disabled="!isConfirmed || isLoading || isLoadingPreview || isBlocked"
          :loading="isLoading"
          text="Confirmar"
          @click="handleRemove"
        />
      </div>
    </template>
  </modal-vue>
</template>

<script lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useErrorToast } from '@/composables/useErrorToast'
import Network from '@/Network'
import type { ScheduleDeletionPreviewResponse } from '@/Network/Types/Responses/ScheduleUnitResponses'
import { getApiErrorStatus } from '@/utils/apiError'

const plural = (count: number, singular: string, pluralText: string) =>
  `${count} ${count === 1 ? singular : pluralText}`

export default {
  components: {
    ModalVue,
    OutlineButton,
    FilledButton
  },
  props: {
    scheduleId: { required: true, type: Number },
    scheduleName: { required: true, type: String }
  },
  setup(props) {
    const modalName = EModals.REMOVE_SCHEDULE
    const { notifyError } = useErrorToast()
    const scheduleStore = useScheduleStore()
    const modalStore = useModalStore()
    const { modals } = storeToRefs(useModalStore())

    const confirmationText = ref('')
    const isLoading = ref(false)
    const isLoadingPreview = ref(false)
    // Q6: resumo dos plantões futuros. `null` = sem resumo (backend sem a rota
    // ou falha ao carregar) — o DELETE continua protegido no backend.
    const preview = ref<ScheduleDeletionPreviewResponse | null>(null)

    const isConfirmed = computed(() => {
      return confirmationText.value === props.scheduleName
    })

    const futureStationsText = computed(() =>
      plural(preview.value?.futureStations ?? 0, 'plantão futuro', 'plantões futuros')
    )
    const futureVacantText = computed(() => {
      const count = preview.value?.futureVacant ?? 0
      return count === 1
        ? '1 vago (sem médico) será excluído junto com a escala'
        : `${count} vagos (sem médico) serão excluídos junto com a escala`
    })
    const futureWithDoctorText = computed(() =>
      plural(preview.value?.futureWithDoctor ?? 0, 'com médico', 'com médico')
    )
    const futureUnidentifiedText = computed(() =>
      plural(
        preview.value?.futureUnidentified ?? 0,
        'ocupado sem identificação',
        'ocupados sem identificação'
      )
    )

    const isBlocked = computed(
      () =>
        (preview.value?.futureWithDoctor ?? 0) > 0 || (preview.value?.futureUnidentified ?? 0) > 0
    )

    const blockedMessage = computed(() => {
      if (!isBlocked.value || !preview.value) return ''
      const parts: string[] = []
      const { futureWithDoctor, futureUnidentified } = preview.value
      if (futureWithDoctor > 0) {
        parts.push(
          plural(futureWithDoctor, 'plantão futuro com médico', 'plantões futuros com médico')
        )
      }
      if (futureUnidentified > 0) {
        parts.push(
          plural(
            futureUnidentified,
            'plantão futuro ocupado sem identificação',
            'plantões futuros ocupados sem identificação'
          )
        )
      }
      return (
        `Não é possível excluir: a escala tem ${parts.join(' e ')}. ` +
        'Realoque ou remova esses plantões antes de excluir a escala.'
      )
    })

    const loadPreview = async () => {
      if (!props.scheduleId) return
      try {
        isLoadingPreview.value = true
        preview.value = await Network.schedule.getDeletionPreview(props.scheduleId)
      } catch (error) {
        console.error(error)
        preview.value = null
        notifyError(error, 'Não foi possível carregar os plantões futuros da escala.')
      } finally {
        isLoadingPreview.value = false
      }
    }

    watch(
      () => modals.value[modalName],
      (isOpen) => {
        if (isOpen) {
          loadPreview()
          return
        }
        confirmationText.value = ''
        preview.value = null
      },
      { immediate: true }
    )

    const handleToggleModal = () => {
      modalStore.toggleModal(modalName)
    }

    const handleRemove = async () => {
      if (!props.scheduleId || !isConfirmed.value || isBlocked.value) return
      try {
        isLoading.value = true
        await scheduleStore.remove(props.scheduleId)
        modalStore.alertDescription = 'Escala excluida com sucesso!'
        modalStore.alertMaxWidth = 'max-w-[525px]'
        modalStore.toggleModal(modalName)
        modalStore.toggleShowAlert()
      } catch (error) {
        console.error(error)
        // `ScheduleUnitService.remove` normaliza em ServiceError, então
        // `axios.isAxiosError` nunca casava e a regra de negócio do backend
        // ("escala com plantões aprovados não pode ser excluída") se perdia.
        notifyError(error, 'Não foi possível excluir a escala.')
        // Q6: 409 = alguém alocou médico num plantão futuro depois do resumo.
        if (getApiErrorStatus(error) === 409) await loadPreview()
      } finally {
        isLoading.value = false
      }
    }

    return {
      modalName,
      modals,
      confirmationText,
      isConfirmed,
      isLoading,
      isLoadingPreview,
      preview,
      futureStationsText,
      futureVacantText,
      futureWithDoctorText,
      futureUnidentifiedText,
      isBlocked,
      blockedMessage,
      handleToggleModal,
      handleRemove
    }
  }
}
</script>
